import React, { useMemo } from 'react'
import clsx from 'clsx'
import {
  WppActionButton,
  WppIconClose,
  WppIconInfo,
  WppIconSuccess,
  WppIconWarning,
  WppTypography,
} from '@wppopen/components-library-react'
import type { BannerTypes } from './BannerTypesUtils.d'
import styles from './Banner.module.scss'

interface BannerProps {
  text: string | React.ReactNode
  type?: BannerTypes
  onBannerClick?: () => void
  onClose?: () => void
  className?: string
  shouldBannerShow?: boolean
  isBannerFixedPosition?: boolean
}

export default function Banner({
  text,
  type = 'success',
  onBannerClick,
  onClose,
  className,
  shouldBannerShow,
  isBannerFixedPosition = true,
}: BannerProps) {
  const renderCorrectIcon = useMemo(() => {
    switch (type) {
      case 'info':
        return <WppIconInfo />
      case 'error':
        return <WppIconWarning />
      default:
        return <WppIconSuccess />
    }
  }, [type])

  if (!shouldBannerShow) return null

  return (
    <div
      className={clsx(
        styles.banner,
        {
          [styles.fixedPosition]: isBannerFixedPosition, // default is fixed to top of the page
          [styles.bannerAsButton]: !!onBannerClick,
          [styles.info]: type === 'info',
          [styles.error]: type === 'error',
        },
        className,
      )}
      data-testid="banner"
    >
      <div
        role={onBannerClick ? 'button' : 'none'}
        onClick={onBannerClick}
        data-testid="inner-banner"
        className={clsx({
          [styles.bannerAsButton]: !!onBannerClick,
        })}
      >
        {renderCorrectIcon}
        <WppTypography tag="p" type="s-body" className={styles.renderText}>
          {text}
        </WppTypography>
      </div>

      {onClose && (
        <WppActionButton
          variant="secondary"
          onClick={onClose}
          aria-label="close-banner"
          data-testid="close-button-banner"
        >
          <WppIconClose slot="icon-start" />
        </WppActionButton>
      )}
    </div>
  )
}
